import ReactDOM from "react-dom/client";
import React, { Suspense, lazy } from "react";
import "./index.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { themeMap } from "./framework/themes";
import { EnvProvider } from "./framework/contexts/env-context";
import { useVaultDomain } from "./framework/hooks/use-vault-domain";
import { clientName } from "./lib/utils";
import { isLoggedIn } from "./framework/auth/auth-utils";
import InlineWrapper from "./framework/components/InlineWapper";
import FullWidget from "./framework/components/FullWidget";

const SignIn = lazy(() => import("./framework/components/SignIn"));
const Dashboard = lazy(() => import("./framework/components/Dashboard"));
const Protected = lazy(() => import("./framework/components/RouteProtected"));
const TrsApp = lazy(() => import("./apps/trs-app/TrsApp"));
const WrappedPayrollSync = lazy(() => import("./apps/payroll-sync/PayrollSync"));
const WidgetCenter = lazy(() => import("./framework/components/WidgetCenter"));
const TaxParserButton = lazy(() => import("./apps/tax-parser/TaxParserButton"));
const BGVInitiator = lazy(() => import("./apps/bgv-initiator/BGVInitiator"));
const ChatBot = lazy(() => import("./apps/chat-bot/ChatBotLauncher"));
const AppDetails = lazy(() => import("./marketplace/widgets/app-details/AppDetails"));
const AppHeader = lazy(() => import("./marketplace/widget-molecules/app-header/AppHeaderLauncher"));
const AppTabs = lazy(() => import("./marketplace/widget-atoms/app-tabs/AppTabsLauncher"));
const CustomQuote = lazy(() => import("./marketplace/widget-atoms/custom-quote/CustomQuoteLauncher"));
const AppCustomQuote = lazy(() => import("./marketplace/widgets/app-custom-quote/AppCustomQuote"));
const EnableApp = lazy(() => import("./marketplace/widget-atoms/enable-app/EnableAppLauncher"));
const AssessmentEnable = lazy(() => import("./marketplace/widget-atoms/app-assessment/AssessmentEnableLauncher"));
const BgvInline = lazy(() => import("./apps/bgv-inline/BgvInline"));
const AssignAssessment = lazy(() => import("./apps/assessment/assign-assessment/AssignAssessment"));
const AssessmentSettings = lazy(()=> import("./apps/assessment/assessment-settings/AssessmentSettings"));
const Billing = lazy(() => import("./apps/billing/Billing"));

themeMap[clientName]?.()
  .then(() => console.log(`${clientName} theme loaded`, themeMap[clientName]))
  .catch((err) => console.error("Failed to load theme", err));

const App = () => {
  const vaultUrl = useVaultDomain();

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="/" element={isLoggedIn() ? <Navigate to="/dashboard" replace /> : <SignIn />} />
        <Route path="/signin" element={<SignIn />} />
        <Route
          path="/dashboard"
          element={<Protected><WidgetCenter><Dashboard /></WidgetCenter></Protected>}
        />
        <Route
          path="/trs-app"
          element={<Protected><WidgetCenter><TrsApp vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
        <Route
          path="/payroll-sync"
          element={<Protected><WidgetCenter><WrappedPayrollSync vaultUrl={vaultUrl} props={{ appId: 'zohobooks' }} /></WidgetCenter></Protected>}
        />
        <Route
          path="/bgv-initiator"
          element={<Protected><WidgetCenter><BGVInitiator vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
        <Route
          path="/tax-parser"
          element={<Protected><WidgetCenter><TaxParserButton vaultUrl={vaultUrl} props={{ appId: 'zohobooks', appName: 'Zoho' }} /></WidgetCenter></Protected>}
        />
         <Route
          path="/chat-bot"
          element={<Protected><WidgetCenter><ChatBot vaultUrl={vaultUrl} chatUrl={""} /></WidgetCenter></Protected>}
        />
         <Route
          path="/app-header"
          element={<Protected><WidgetCenter><AppHeader vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
         <Route
          path="/app-details"
          element={<Protected><WidgetCenter><AppDetails vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
         <Route
          path="/app-tabs"
          element={<Protected><WidgetCenter><AppTabs vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
         <Route
          path="/custom-quote"
          element={<Protected><WidgetCenter><CustomQuote vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
         <Route
          path="/app-custom-quote"
          element={<Protected><WidgetCenter><AppCustomQuote vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
        <Route
          path="/enable-app"
          element={<Protected><WidgetCenter><EnableApp vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
        <Route
          path="/assessment-app"
          element={<Protected><WidgetCenter><AssessmentEnable vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
        <Route
          path="/bgv-inline"
          element={<Protected><WidgetCenter><InlineWrapper><BgvInline vaultUrl={vaultUrl} /></InlineWrapper></WidgetCenter></Protected>}
        />
        <Route
          path="/assign-assessment"
          element={<Protected><WidgetCenter><AssignAssessment vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
        <Route
          path="/assessment-settings"
          element={<Protected><WidgetCenter><AssessmentSettings vaultUrl={vaultUrl} /></WidgetCenter></Protected>}
        />
        <Route
          path="/billing"
          element={<Protected><FullWidget><Billing vaultUrl={vaultUrl} /></FullWidget></Protected>}
        />
      </Routes>
    </Suspense>
  );
};

const container = document.getElementById("app") as HTMLElement;

ReactDOM.createRoot(container).render(
  <BrowserRouter>
    <EnvProvider>
      <App />
    </EnvProvider>
  </BrowserRouter>
);
